import React, { useEffect, useRef, useCallback, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useDispatch, useSelector } from "react-redux";
import { fetchRecordedContacts } from "../../../../store/RecordedContacts/recordedContactsThunks";
import { getRecordedContactColumns } from "./RecordedContactDataGridColumns";
import RecordedContactCustomToolbar from "./RecordedContactCustomToolbar";
import DetailPanelContent from "./DetailPanelContent";

function RecordedContactDataGrid({
  onEditContact,
  onAddContact,
  searchQuery,
  onSearch,
  apiRef,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { contacts, loading, currentPage, hasMore } = useSelector(
    (state) => state.recordedContacts
  );
  const containerRef = useRef(null);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);

  // Initial load
  useEffect(() => {
    dispatch(fetchRecordedContacts({ page: 1, search: searchQuery }));
  }, [dispatch, searchQuery]);

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    dispatch(
      fetchRecordedContacts({
        page: 1,
        filters: filterModel.items.filter(
          (filter) => filter.value !== undefined && filter.value !== ""
        ),
        sortModel: newSortModel,
        search: searchQuery,
      })
    );
  };

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    const validFilters = newFilterModel.items.filter(
      (filter) => filter.value !== undefined && filter.value !== ""
    );
    if (validFilters.length > 0) {
      dispatch(
        fetchRecordedContacts({
          page: 1,
          filters: validFilters,
          sortModel: sortModel,
          search: searchQuery,
        })
      );
    }
  };

  const handleRefresh = () => {
    dispatch(fetchRecordedContacts({ page: 1, search: searchQuery }));
  };

  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return;
    const container = containerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      dispatch(
        fetchRecordedContacts({
          page: currentPage + 1,
          search: searchQuery,
        })
      );
    }
  }, [dispatch, loading, hasMore, currentPage, searchQuery]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <Box sx={{ height: "100%", width: "100%" }} ref={containerRef}>
      <DataGridPro
        apiRef={apiRef}
        rows={contacts}
        columns={getRecordedContactColumns(onEditContact)}
        disableExtendRowFullWidth
        sortingMode="server"
        filterMode="server"
        filterDebounceMs={500}
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={handleFilterModelChange}
        loading={loading}
        autoHeight={false}
        hideFooter
        getDetailPanelContent={({ row }) => (
          <DetailPanelContent
            row={row}
            filterModel={filterModel}
            sortModel={sortModel}
            searchQuery={searchQuery}
          />
        )}
        slotProps={{
          filterPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              '& .MuiButton-root': {
                backgroundColor: (theme) => theme.palette.background.button,
                color: (theme) => theme.palette.common.white,
                textTransform: "none",
                px: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.background.button,
                },
              },
              '& .MuiIconButton-root': {
                color: theme.palette.text.primary,
              },
            }
          },
          columnsPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          },
        }}
        getDetailPanelHeight={() => "auto"}
        hideDetailPanelToggle={true}
        initialState={{
          columns: {
            columnVisibilityModel: {
              __detail_panel_toggle__: false,
              competence: false,
              status: false,
              function: false,
              route: false,
              area: false,
              retentionPolicy: false,
              internalId: false,
              sentinelNumber: false,
              empCompFrom: false,
              empCompValidUntil: false,
              supervisorEmployeeNumber: false,
              supervisorEmailAddress: false,
              supervisorName: false,
              supervisorEmail: false,
              positionName: false,
              costCentre: false,
              organisation: false,
              supervisorMvrRole: false,
            },
          },
        }}
      />
    </Box>
  );
}

export default RecordedContactDataGrid;
